import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Row, Col, InputGroup, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaPlus, FaEdit, FaTrash, FaSortUp, FaSortDown, FaSort, FaSearch } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import AuthStorage from '~/Utils/AuthStorage';
import * as api from '~/Services/Service';
import ModalCategoriaContas from './ModalCategoriaContas';
import { TIPOS_CONTA } from './constants';

const REGISTROS_POR_PAGINA = 20;

const CategoriasContas = () => {
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [categoriaEditando, setCategoriaEditando] = useState(null);
  const [confirmacao, setConfirmacao] = useState({ show: false, item: null });
  const [filtro, setFiltro] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroAplicado, setFiltroAplicado] = useState('');
  const [filtroTipoAplicado, setFiltroTipoAplicado] = useState('');
  const [ordenacao, setOrdenacao] = useState({ campo: 'codigo', direcao: 'asc' });
  const [paginaAtual, setPaginaAtual] = useState(1);

  const temPermissaoEscrita = AuthStorage && AuthStorage.temCaracteristicas([261]);

  useEffect(() => {
    carregarCategorias();
  }, []);

  const carregarCategorias = async () => {
    setLoading(true);
    try {
      const resp = await api.buscarCategoriaContas();
      setCategorias(resp || []);
    } catch (e) {
      toast.error('Erro ao carregar categorias de contas.');
    } finally {
      setLoading(false);
    }
  };

  const handleOrdenacao = (campo) => {
    setOrdenacao((prev) => {
      if (prev.campo === campo) {
        return { campo: campo, direcao: prev.direcao === 'asc' ? 'desc' : 'asc' };
      }
      return { campo: campo, direcao: 'asc' };
    });
  };

  const handlePesquisar = () => {
    setFiltroAplicado(filtro);
    setFiltroTipoAplicado(filtroTipo);
    setPaginaAtual(1);
  };

  const handleLimparFiltros = () => {
    setFiltro('');
    setFiltroTipo('');
    setFiltroAplicado('');
    setFiltroTipoAplicado('');
    setPaginaAtual(1);
  };

  const iconeOrdenacao = (campo) => {
    if (ordenacao.campo !== campo) return <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />;
    return ordenacao.direcao === 'asc'
      ? <FaSortUp style={{ marginLeft: 4 }} />
      : <FaSortDown style={{ marginLeft: 4 }} />;
  };

  const thSortStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

  const handleNova = () => {
    setCategoriaEditando(null);
    setShowModal(true);
  };

  const handleEditar = (item) => {
    setCategoriaEditando(item);
    setShowModal(true);
  };

  const handleExcluir = (item) => {
    setConfirmacao({ show: true, item: item });
  };

  const handleConfirmarExclusao = async (confirmado) => {
    if (!confirmado) {
      setConfirmacao({ show: false, item: null });
      return;
    }
    const item = confirmacao.item;
    setConfirmacao({ show: false, item: null });
    try {
      await api.excluirCategoriaContas({ codigo: item.codigo });
      toast.success('Categoria excluída com sucesso.');
      carregarCategorias();
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao excluir categoria.';
      toast.error(msg);
    }
  };

  const handleSalvar = () => {
    setShowModal(false);
    carregarCategorias();
  };

  const categoriasFiltradas = categorias.filter((item) => {
    const matchTexto = !filtroAplicado || (
      String(item.codigo).indexOf(filtroAplicado) >= 0 ||
      (item.descricao || '').toLowerCase().indexOf(filtroAplicado.toLowerCase()) >= 0
    );
    const matchTipo = !filtroTipoAplicado || String(item.tipo) === String(filtroTipoAplicado);
    return matchTexto && matchTipo;
  });

  const categoriasOrdenadas = categoriasFiltradas.slice().sort((a, b) => {
    const campo = ordenacao.campo;
    const dir = ordenacao.direcao === 'asc' ? 1 : -1;
    let valA = a[campo];
    let valB = b[campo];
    if (valA == null) valA = '';
    if (valB == null) valB = '';
    if (typeof valA === 'string') {
      return dir * valA.toLowerCase().localeCompare(String(valB).toLowerCase());
    }
    return dir * (valA - valB);
  });

  const totalPaginas = Math.max(1, Math.ceil(categoriasOrdenadas.length / REGISTROS_POR_PAGINA));
  const paginaCorrigida = Math.min(paginaAtual, totalPaginas);
  const inicio = (paginaCorrigida - 1) * REGISTROS_POR_PAGINA;
  const fim = inicio + REGISTROS_POR_PAGINA;
  const categoriasPaginadas = categoriasOrdenadas.slice(inicio, fim);

  const irParaPagina = (p) => {
    if (p < 1 || p > totalPaginas) return;
    setPaginaAtual(p);
  };

  const itemExclusao = confirmacao.item;

  if (loading) return <Loading />;

  return (
    <Container fluid className="px-3 py-3">
      <style>{'\
        .catcontas-desktop { display: block; }\
        .catcontas-mobile  { display: none; }\
        @media (max-width: 768px) {\
          .catcontas-desktop { display: none !important; }\
          .catcontas-mobile  { display: block; }\
          .catcontas-filtro-row { flex-direction: column; }\
          .catcontas-filtro-row .text-right { text-align: right !important; margin-top: 8px; }\
        }\
      '}</style>

      <Header title="Categorias de Contas" />

      <Card className="shadow-sm mb-3">
        <Card.Body className="py-2">
          <Row className="align-items-center catcontas-filtro-row">
            <Col md={4}>
              <InputGroup size="sm">
                <InputGroup.Prepend>
                  <InputGroup.Text><FaSearch /></InputGroup.Text>
                </InputGroup.Prepend>
                <Form.Control
                  placeholder="Filtrar por código ou descrição..."
                  value={filtro}
                  onChange={(e) => { setFiltro(e.target.value); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { handlePesquisar(); } }}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Control
                as="select"
                size="sm"
                value={filtroTipo}
                onChange={(e) => { setFiltroTipo(e.target.value); }}
              >
                <option value="">Todos os tipos</option>
                {TIPOS_CONTA.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </Form.Control>
            </Col>
            <Col md="auto">
              <Button size="sm" variant="primary" onClick={handlePesquisar} className="mr-1">
                <FaSearch className="mr-1" /> Pesquisar
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={handleLimparFiltros}>
                Limpar
              </Button>
            </Col>
            <Col className="text-right">
              {temPermissaoEscrita && (
                <Button size="sm" variant="primary" onClick={handleNova}>
                  <FaPlus className="mr-1" /> Nova Categoria
                </Button>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Header
          className="d-flex justify-content-between align-items-center py-2"
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
        >
          <strong>
            Categorias de Contas
            <span className="ml-2" style={{ fontSize: '0.85rem', opacity: 0.8 }}>
              ({categoriasOrdenadas.length} {categoriasOrdenadas.length === 1 ? 'registro' : 'registros'})
            </span>
          </strong>
        </Card.Header>
        <Card.Body className="p-0">
          {categoriasOrdenadas.length === 0 ? (
            <p className="text-muted text-center my-4">Nenhuma categoria cadastrada.</p>
          ) : (
            <>
              {/* Desktop */}
              <div className="catcontas-desktop">
                <Table striped bordered hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th
                        style={Object.assign({ width: '90px' }, thSortStyle)}
                        onClick={() => { handleOrdenacao('codigo'); }}
                      >
                        Código{iconeOrdenacao('codigo')}
                      </th>
                      <th
                        style={Object.assign({ width: '220px' }, thSortStyle)}
                        onClick={() => { handleOrdenacao('descricao'); }}
                      >
                        Descrição{iconeOrdenacao('descricao')}
                      </th>
                      <th
                        style={Object.assign({ width: '120px' }, thSortStyle)}
                        onClick={() => { handleOrdenacao('faixa_ini'); }}
                      >
                        Faixa Inicial{iconeOrdenacao('faixa_ini')}
                      </th>
                      <th
                        style={Object.assign({ width: '120px' }, thSortStyle)}
                        onClick={() => { handleOrdenacao('faixa_fim'); }}
                      >
                        Faixa Final{iconeOrdenacao('faixa_fim')}
                      </th>
                      <th
                        style={Object.assign({ width: '180px' }, thSortStyle)}
                        onClick={() => { handleOrdenacao('tipo_descricao'); }}
                      >
                        Tipo{iconeOrdenacao('tipo_descricao')}
                      </th>
                      <th style={{ width: '90px', textAlign: 'center' }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoriasPaginadas.map((item) => (
                      <tr key={item.codigo}>
                        <td>{item.codigo}</td>
                        <td>{item.descricao}</td>
                        <td>{item.faixa_ini}</td>
                        <td>{item.faixa_fim}</td>
                        <td>{item.tipo_descricao || ''}</td>
                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                          {temPermissaoEscrita && (
                            <>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                title="Editar"
                                className="mr-1"
                                onClick={() => { handleEditar(item); }}
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                title="Excluir"
                                onClick={() => { handleExcluir(item); }}
                              >
                                <FaTrash />
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Mobile */}
              <div className="catcontas-mobile p-2">
                {categoriasPaginadas.map((item) => (
                  <Card key={item.codigo} className="mb-2 shadow-sm">
                    <Card.Body className="py-2 px-3">
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <div>
                          <strong style={{ fontSize: '0.95rem' }}>{item.descricao}</strong>
                          <br />
                          <small className="text-muted">Código: {item.codigo}</small>
                        </div>
                      </div>
                      <div style={{ fontSize: '0.85rem' }} className="mb-1">
                        Faixa: <strong>{item.faixa_ini}</strong> — <strong>{item.faixa_fim}</strong>
                      </div>
                      <div style={{ fontSize: '0.85rem' }} className="mb-2">
                        Tipo: <strong>{item.tipo_descricao || '—'}</strong>
                      </div>
                      {temPermissaoEscrita && (
                        <div className="d-flex justify-content-end">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="mr-1"
                            onClick={() => { handleEditar(item); }}
                          >
                            <FaEdit className="mr-1" /> Editar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => { handleExcluir(item); }}
                          >
                            <FaTrash className="mr-1" /> Excluir
                          </Button>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </>
          )}
        </Card.Body>
        {categoriasOrdenadas.length > 0 && (
          <Card.Footer className="py-2">
            <Row className="align-items-center">
              <Col xs={12} md={6}>
                <small className="text-muted">
                  Exibindo {inicio + 1}-{Math.min(fim, categoriasOrdenadas.length)} de {categoriasOrdenadas.length}
                </small>
              </Col>
              <Col xs={12} md={6} className="text-md-right mt-2 mt-md-0">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="mr-1"
                  disabled={paginaCorrigida <= 1}
                  onClick={() => { irParaPagina(1); }}
                >
                  «
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="mr-1"
                  disabled={paginaCorrigida <= 1}
                  onClick={() => { irParaPagina(paginaCorrigida - 1); }}
                >
                  ‹
                </Button>
                <span className="mx-2" style={{ fontSize: '0.9rem' }}>
                  Página {paginaCorrigida} de {totalPaginas}
                </span>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="mr-1"
                  disabled={paginaCorrigida >= totalPaginas}
                  onClick={() => { irParaPagina(paginaCorrigida + 1); }}
                >
                  ›
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={paginaCorrigida >= totalPaginas}
                  onClick={() => { irParaPagina(totalPaginas); }}
                >
                  »
                </Button>
              </Col>
            </Row>
          </Card.Footer>
        )}
      </Card>

      <ModalConfirmacao
        show={confirmacao.show}
        title="Confirmar Exclusão"
        message={
          itemExclusao
            ? 'Deseja excluir a categoria "' + (itemExclusao.descricao || '') + '" (Código ' + itemExclusao.codigo + ')?'
            : ''
        }
        onClose={handleConfirmarExclusao}
      />

      <ModalCategoriaContas
        show={showModal}
        onHide={() => { setShowModal(false); }}
        categoriaEditando={categoriaEditando}
        categoriasExistentes={categorias}
        onSalvar={handleSalvar}
      />
    </Container>
  );
};

export default CategoriasContas;
